import { useEffect, useRef, useState } from 'react';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { startCheckout } from '../../api/billing';
import { rememberCheckoutOffer, reportInitiateCheckout, type Offer } from '../../state/trial';
import type { PlanId } from '../../state/plans';
import { leaveFor } from '../../platform/leave';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';
import { secondsBucket } from '../../analytics/visits';
import Paywall from './Paywall';
import ExitQuestion, { exitQuestionAsked } from './ExitQuestion';

/**
 * The offer to a free-week account (V6, 2026-10-14): keep the app live after its week. Since
 * 2026-10-27 it is the plan choice: the card taken now and first charged on the free-until date —
 * the server makes the week's days left the plan's trial — or at once, once the week has ended and
 * the app is paused. Only the two accounts still on a free week see this. Over the live screen
 * (Live.tsx) or the editor on a later visit. "Not now" asks once, ever, what stopped them
 * (ExitQuestion), then `onClose`.
 */
export default function FreeWeekOffer({ appName, until, ownerId, appId, onClose }: {
  appName: string;
  /** ISO: the free week's end. */
  until: string;
  ownerId: string;
  appId: string;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [askingExit, setAskingExit] = useState(false);
  // While the week runs the plan's trial ends on its last day; after it, the plan starts today.
  const [offer] = useState<Offer>(() => (Date.parse(until) > Date.now() ? 'trial' : 'subscribe'));
  useFunnelStep('paywall_viewed', true, { offer });

  // Back from Stripe by the browser's Back (the page restored as it was left): the button is given back.
  useEffect(() => {
    const shown = (e: PageTransitionEvent): void => {
      if (e.persisted) setBusy(false);
    };
    window.addEventListener('pageshow', shown);
    return () => window.removeEventListener('pageshow', shown);
  }, []);

  // paywall_left, as on the plan and the reveal: put away, or the page closed with it open.
  const openedAt = useRef<number | null>(Date.now());
  const toCheckout = useRef(false);
  function noteLeft(now: boolean): void {
    if (openedAt.current === null || toCheckout.current) return;
    funnelStep('paywall_left', { offer, seconds: secondsBucket(Date.now() - openedAt.current) }, now ? { now: true } : undefined);
    openedAt.current = null;
  }
  useEffect(() => {
    const onHide = (): void => noteLeft(true);
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  });

  async function checkout(plan: PlanId): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    rememberCheckoutOffer(offer, offer === 'trial' ? { plan, chargeAt: until } : { plan });
    const result = await startCheckout('subscription', (url) => {
      toCheckout.current = true;
      funnelStep('checkout_started', { offer }, { now: true });
      // Keyed by the app, not an idea: one InitiateCheckout per app kept live.
      if (reportInitiateCheckout(ownerId, `keep-live:${appId}`)) setTimeout(() => leaveFor(url), 300);
      else leaveFor(url);
    }, { plan });
    if (result.status === 'redirecting') return;
    setBusy(false);
    setNotice(result.status === 'signin_required' ? 'Your session has ended. Sign in again to keep it live.' : result.message);
  }

  const plan = { appName, features: [] } as unknown as AppPlan;
  return (
    // Over a dimmed page (the scrim): the site, or the editor, is behind the sheet, not beside it.
    <div className="site-offer site-offer-open site-offer-scrim" aria-live="polite">
      {askingExit ? (
        <div className="paywall"><ExitQuestion freeWeek onDone={() => { setAskingExit(false); onClose(); }} /></div>
      ) : (
        <Paywall
          plan={plan}
          offer={offer}
          freeWeekUntil={until}
          busy={busy}
          notice={notice ? <p role="alert" data-error-code="checkout" className="type-caption field-error-text">{notice}</p> : undefined}
          backLabel="Not now"
          onContinue={(plan) => void checkout(plan)}
          onBack={() => {
            noteLeft(false);
            if (exitQuestionAsked()) onClose();
            else setAskingExit(true);
          }}
        />
      )}
    </div>
  );
}
